import React from 'react';
import { Shield, RefreshCw, Lock, FileText, Mail, CreditCard } from 'lucide-react';

export default function PrivacyRefund() {
  const supportEmail = "support@yourdomain.com"; // ⚠️ CHANGE THIS TO YOUR ACTUAL EMAIL
  const companyName = "Your Company Name"; // ⚠️ CHANGE THIS
  const lastUpdated = "October 23, 2025"; // ⚠️ UPDATE THIS

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Shield className="w-12 h-12 text-blue-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Privacy & Refund Policy</h1>
          </div>
          <p className="text-gray-600">Last Updated: {lastUpdated}</p>
        </div>

        {/* Privacy Policy Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center mb-6">
            <Lock className="w-8 h-8 text-blue-600 mr-3" />
            <h2 className="text-3xl font-bold text-gray-900">Privacy Policy</h2>
          </div>

          <div className="space-y-6 text-gray-700">
            <section className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <p className="text-lg font-semibold text-gray-900 mb-2">Simple & Transparent</p>
              <p>
                We believe in keeping things simple. Here's exactly what we collect and why.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                What We Collect
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2 mt-1">•</span>
                  <div>
                    <strong>Email Address:</strong> For account login and communication
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2 mt-1">•</span>
                  <div>
                    <strong>PDF Files:</strong> The documents you upload to process (temporarily stored)
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2 mt-1">•</span>
                  <div>
                    <strong>Payment Information:</strong> Handled securely by Stripe (we never see your card details)
                  </div>
                </li>
              </ul>
              <p className="mt-4 text-sm text-gray-600 italic">
                That's it. We don't collect browsing history, location data, or anything else unnecessary.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">How We Use Your Information</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>To provide our PDF processing service</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>To send you important service updates via email</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>To process payments through Stripe</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                <CreditCard className="w-5 h-5 mr-2 text-blue-600" />
                Payment Security (Stripe)
              </h3>
              <p className="mb-3">
                All payments are processed by <strong>Stripe</strong>, a PCI-compliant payment processor 
                trusted by millions of businesses worldwide.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800">
                  <strong>🔒 Your card information is secure:</strong> We never see, store, or have 
                  access to your credit card details. Stripe handles all payment data with 
                  bank-level security.
                </p>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Your PDF Files</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>PDFs are stored temporarily to process your requests</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Files are securely stored with Firebase/Google Cloud</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>We do not share your files with anyone</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Sharing</h3>
              <p className="mb-3">
                <strong>We do not sell or share your data.</strong> The only third parties with access are:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span><strong>Firebase/Google Cloud:</strong> For secure data storage</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span><strong>Stripe:</strong> For payment processing only</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Your Rights</h3>
              <p className="mb-2">You can:</p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Request deletion of your account and data</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Download your data</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Contact us with any privacy concerns</span>
                </li>
              </ul>
            </section>
          </div>
        </div>

        {/* Refund Policy Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center mb-6">
            <RefreshCw className="w-8 h-8 text-green-600 mr-3" />
            <h2 className="text-3xl font-bold text-gray-900">Refund Policy</h2>
          </div>

          <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-6 rounded-r-lg">
            <p className="text-lg text-gray-800 font-semibold mb-2">
              💚 100% Satisfaction Guarantee
            </p>
            <p className="text-gray-700">
              Not happy? We'll make it right. Your satisfaction is our priority.
            </p>
          </div>

          <div className="space-y-6 text-gray-700">
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">7-Day Money-Back Guarantee</h3>
              <p className="mb-3">
                If you're not satisfied with our service for any reason, we offer a full refund 
                within <strong>7 days of your purchase</strong>.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">When We Offer Refunds</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Service doesn't work as described</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Technical issues preventing you from using the service</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>You're simply not satisfied (within 7 days)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Accidental duplicate charges</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">How to Request a Refund</h3>
              <div className="bg-blue-50 rounded-lg p-6">
                <ol className="space-y-3">
                  <li className="flex items-start">
                    <span className="font-bold text-blue-600 mr-2">1.</span>
                    <div>
                      Email us at{' '}
                      <a href={`mailto:${supportEmail}`} className="text-blue-600 hover:underline font-semibold">
                        {supportEmail}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold text-blue-600 mr-2">2.</span>
                    <span>Include your account email and reason for the refund</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold text-blue-600 mr-2">3.</span>
                    <span>We'll process your request within 2-3 business days</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold text-blue-600 mr-2">4.</span>
                    <span>Refunds appear in your account within 5-10 business days</span>
                  </li>
                </ol>
              </div>
            </section>

            <section className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">💡 Try Support First?</h3>
              <p className="mb-3">
                Most issues can be resolved quickly! Before requesting a refund, let us know 
                what's wrong. We're here to help and want you to have a great experience.
              </p>
              <p className="text-sm text-gray-600">
                Many users find their issues resolved within hours, and we'd love the chance 
                to make things right for you too.
              </p>
            </section>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-xl p-8 text-white text-center">
          <Mail className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-3">Questions?</h2>
          <p className="text-blue-100 mb-6">
            We're here to help with any privacy or refund concerns
          </p>
          <a
            href={`mailto:${supportEmail}`}
            className="inline-block bg-white text-blue-600 font-semibold px-8 py-3 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Email Us
          </a>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-600 mt-8 text-sm">
          <p>© 2025 {companyName}. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}